import React from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
} from "@mui/material";
import { Sort } from "@mui/icons-material";

const ChallengeFilters = ({
  categories,
  categoryFilter,
  handleCategoryFilterChange,
  statusFilter,
  handleStatusFilterChange,
  search,
  handleSearchChange,
  sortBy,
  sortOrder,
  handleSort,
  clearFilters,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "center",
        marginBottom: "20px",
        minWidth: "100%",
        padding: "20px 15px",
        backgroundColor: "#fff",
        border: "1px solid #ddd",
        borderRadius: "4px",
        height: "auto",
        // minHeight: '60px',
        width: "100%",
        boxSizing: "border-box",
        justifyContent: "flex-start",
        "@media (min-width: 768px)": {
          justifyContent: "space-around",
        },
      }}
    >
      {/* Category Filter */}
      <FormControl sx={{ minWidth: 140, maxWidth: 300, flexShrink: 1 }}>
        <InputLabel
          id="category-filter-label"
          sx={{
            color: "gray",
            fontSize: "11px",
            "&.Mui-focused": {
              color: "#142C5C !important",
            },
          }}
        >
          Category
        </InputLabel>
        <Select
          labelId="category-filter-label"
          id="category-filter-select"
          multiple
          value={categoryFilter}
          onChange={handleCategoryFilterChange}
          label="Category"
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((id) => {
                const cat = categories.find((c) => c._id === id);
                return (
                  <Chip
                    key={id}
                    label={cat?.name || id}
                    size="small"
                    sx={{ height: "20px", fontSize: "10px" }}
                  />
                );
              })}
            </Box>
          )}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 200,
              },
            },
            disableScrollLock: true,
          }}
          sx={{
            minHeight: "40px",
            "& .MuiSelect-select": {
              fontSize: "11px",
              color: "black",
              paddingY: "6px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#142C5C",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#142C5C",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#142C5C",
            },
          }}
        >
          {categories.map((category) => (
            <MenuItem key={category._id} value={category._id}>
              <Checkbox
                checked={categoryFilter.indexOf(category._id) > -1}
                size="small"
                sx={{
                  padding: "2px 8px 2px 0",
                  color: "#142C5C",
                  "&.Mui-checked": { color: "#142C5C" },
                }}
              />
              {category.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Status Filter */}
      <FormControl sx={{ minWidth: 140, flexShrink: 1 }}>
        <InputLabel
          id="status-filter-label"
          sx={{
            color: "gray",
            fontSize: "11px",
            "&.Mui-focused": {
              color: "#142C5C !important",
            },
          }}
        >
          Status
        </InputLabel>
        <Select
          labelId="status-filter-label"
          id="status-filter-select"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          label="Status"
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 200,
              },
            },
            disableScrollLock: true,
          }}
          sx={{
            height: "40px",
            "& .MuiSelect-select": {
              fontSize: "11px",
              color: "black",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#142C5C",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#142C5C",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#142C5C",
            },
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="Upcoming">Upcoming</MenuItem>
          <MenuItem value="Scheduled">Scheduled</MenuItem>
          <MenuItem value="In Progress">In Progress</MenuItem>
          <MenuItem value="Ended">Ended</MenuItem>
        </Select>
      </FormControl>

      {/* Search Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
          flexShrink: 1,
        }}
      >
        <input
          placeholder="Search challenges..."
          type="text"
          value={search}
          onChange={handleSearchChange}
          className="search-field-ui"
          style={{ height: "40px", width: "100%" }}
        />
      </Box>

      {/* Sort Controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Button
          variant={sortBy === "startDate" ? "contained" : "outlined"}
          size="small"
          onClick={() => handleSort("startDate")}
          startIcon={<Sort />}
          aria-label={`Sort by start date ${
            sortBy === "startDate"
              ? sortOrder === 1
                ? "ascending"
                : "descending"
              : "ascending"
          }`}
          aria-pressed={sortBy === "startDate"}
          sx={{
            backgroundColor:
              sortBy === "startDate" ? "#142C5C" : "transparent",
            color: sortBy === "startDate" ? "white" : "#142C5C",
            borderColor: "#142C5C",
            borderRadius: "4px",
            padding: "2px 6px",
            height: "32px",
            minWidth: "auto",
            fontSize: "10px",
            "&:hover": {
              backgroundColor:
                sortBy === "startDate"
                  ? "#0f2247"
                  : "rgba(20, 44, 92, 0.04)",
              borderColor: sortBy === "startDate" ? "#0f2247" : "#0f2247",
            },
          }}
        >
          Start{" "}
          {sortBy === "startDate" && (sortOrder === -1 ? "↓" : "↑")}
        </Button>
        <Button
          variant={sortBy === "endDate" ? "contained" : "outlined"}
          size="small"
          onClick={() => handleSort("endDate")}
          startIcon={<Sort />}
          aria-label={`Sort by end date ${
            sortBy === "endDate"
              ? sortOrder === 1
                ? "ascending"
                : "descending"
              : "ascending"
          }`}
          aria-pressed={sortBy === "endDate"}
          sx={{
            backgroundColor:
              sortBy === "endDate" ? "#142C5C" : "transparent",
            color: sortBy === "endDate" ? "white" : "#142C5C",
            borderColor: "#142C5C",
            borderRadius: "4px",
            padding: "2px 6px",
            height: "32px",
            minWidth: "auto",
            fontSize: "10px",
            "&:hover": {
              backgroundColor:
                sortBy === "endDate" ? "#0f2247" : "rgba(20, 44, 92, 0.04)",
              borderColor: sortBy === "endDate" ? "#0f2247" : "#0f2247",
            },
          }}
        >
          End{" "}
          {sortBy === "endDate" && (sortOrder === 1 ? "↑" : "↓")}
        </Button>
      </Box>

      {/* Clear Filters Button */}
      {(categoryFilter.length > 0 ||
        statusFilter ||
        sortBy !== "startDate" ||
        sortOrder !== -1 ||
        search) && (
        <Button
          variant="outlined"
          onClick={clearFilters}
          aria-label="Clear all filters and sorting"
          sx={{
            height: "32px",
            borderColor: "#142C5C",
            color: "#142C5C",
            borderRadius: "4px",
            padding: "0 10px",
            minWidth: "45px",
            flexShrink: 1,
            fontSize: "11px",
            "&:hover": {
              borderColor: "#0f2247",
              backgroundColor: "rgba(20, 44, 92, 0.04)",
            },
          }}
        >
          Clear
        </Button>
      )}
    </Box>
  );
};

export default ChallengeFilters;
